import { useCallback } from 'react'
import {
  asRelatedPresentation, defineEntitySlotsPreference, type RelatedPresentation,
} from '@solum/experience'
import { useUserSettingPreference } from '../data/useUserSettingPreference'

/**
 * Which lens this person uses for each relationship slot.
 *
 * This remains a personal reading preference under `core.user_setting`, not a
 * governed relationship arrangement. The legacy entity-level `lens` is read as
 * an inherited answer and is never rewritten or dropped by a slot edit.
 */
const KEY = 'connections.lens'
const SAVE_DEBOUNCE_MS = 300

type StoredEntity = { lens?: string; slots?: Record<string, string> }
type LensMap = Record<string, StoredEntity>

const preference = defineEntitySlotsPreference<RelatedPresentation>({
  settingKey: KEY,
  decode: asRelatedPresentation,
})

export interface ConnectionsLensState {
  requestedFor: (slots: readonly string[]) => Record<string, RelatedPresentation>
  setLens: (slot: string, lens: RelatedPresentation) => void
  resetLens: (slot: string) => void
  ready: boolean
}

export function useConnectionsLens(entity: string): ConnectionsLensState {
  const state = useUserSettingPreference(
    preference, { entity }, entity, SAVE_DEBOUNCE_MS,
  )
  const stored = ((state.settings?.[KEY] ?? {}) as LensMap)[entity]
  const inherited = asRelatedPresentation(stored?.lens)
  const mine = state.value

  const requestedFor = useCallback((slots: readonly string[]) => {
    const out: Record<string, RelatedPresentation> = {}
    for (const slot of slots) {
      const chosen = mine[slot] ?? inherited
      if (chosen) out[slot] = chosen
    }
    return out
  }, [inherited, mine])

  const setLens = useCallback((slot: string, value: RelatedPresentation) => {
    if (slot) state.update({ slot, value })
  }, [state.update])

  const resetLens = useCallback((slot: string) => {
    if (slot && slot in mine) state.update({ slot, value: null })
  }, [mine, state.update])

  return { requestedFor, setLens, resetLens, ready: state.ready }
}
